<template>
  <div class="relative bg-white pt-16 pb-16">
    <div class="px-4 md:px-10 mx-auto w-full">

      <!-- Table -->
      <div class="flex flex-wrap mt-6">
        <div class="w-full px-4">
          <div class="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-white">
            <div class="rounded-t mb-0 px-6 py-3 border-0">
              <div class="flex flex-wrap items-center">
                <div class="relative w-full px-4 max-w-full flex-grow flex-1">
                  <h3 class="font-semibold text-base text-blueGray-700">
                    活动投诉列表
                  </h3>
                </div>
              </div>
            </div>
            <div class="block w-full px-6 pb-6">
              <div class="mb-4">
                <SearchFilter
                  ref="searchFilter"
                  :search-keys="searchKeys"
                  :value-dropdown-options-map="valueDropdownOptionsMap"
                  :search-key-label-map="searchKeyLabelMap"
                  :show-add-button="false"
                  @search="executeSearch"
                  @clear="clearSearch"
                />
              </div>
              <div class="block w-full overflow-x-auto md:overflow-visible">
              <el-table
                :data="paginatedComplaints"
                style="width: 100%"
                :default-sort="{ prop: 'id', order: 'descending' }"
                stripe
                border
                highlight-current-row
              >
                <el-table-column label="投诉人" width="220">
                  <template #default="scope">
                    <div class="flex items-center">
                      <div class="ml-3">
                        <div class="font-medium">{{ scope.row.unionid }}</div>
                        <div class="text-sm text-gray-500">{{ scope.row.mobile }}</div>
                      </div>
                    </div>
                  </template>
                </el-table-column>

                <el-table-column label="活动名称" width="250" show-overflow-tooltip>
                  <template #default="scope">
                    <div class="flex items-center">
                      <div class="ml-3">
                        <div class="font-medium">{{ scope.row.activityName }}</div>
                        <div class="text-sm text-gray-500">{{ scope.row.tid }}</div>
                      </div>
                    </div>
                  </template>
                  </el-table-column>

                <el-table-column label="投诉类型" width="150">
                  <template #default="scope">
                    <el-tag
                      :type="getTypeTagType(scope.row.type)"
                      size="small"
                    >
                      {{ getTypeLabel(scope.row.type) }}
                    </el-tag>
                  </template>
                </el-table-column>

                <el-table-column prop="content" label="投诉内容" min-width="300" show-overflow-tooltip />

                <el-table-column label="状态" width="100">
                  <template #default="scope">
                    <el-tag
                      :type="getStatusTagType(scope.row.status)"
                      size="small"
                    >
                      {{ getStatusLabel(scope.row.status) }}
                    </el-tag>
                  </template>
                </el-table-column>

                <el-table-column prop="createdAt" label="提交时间" width="180" sortable>
                  <template #default="scope">
                    {{ formatDate(scope.row.createdAt * 1000) }}
                  </template>
                </el-table-column>

                <el-table-column label="操作" width="200" fixed="right">
                  <template #default="scope">
                    <el-button
                      type="primary"
                      size="small"
                      @click="viewComplaint(scope.row)"
                    >
                      查看
                    </el-button>
                    <el-button
                      v-if="scope.row.status === 'pending'"
                      type="success"
                      size="small"
                      @click="resolveComplaint(scope.row)"
                    >
                      处理
                    </el-button>
                    <el-button
                      v-if="scope.row.status === 'pending'"
                      type="danger"
                      size="small"
                      @click="dismissComplaint(scope.row)"
                    >
                      驳回
                    </el-button>
                  </template>
                </el-table-column>
              </el-table>
              </div>
              <el-pagination
                :current-page="currentPage"
                :page-size="itemsPerPage"
                :page-sizes="[10, 20, 50, 100]"
                :total="totalRecords"
                layout="total, sizes, prev, pager, next, jumper"
                @size-change="handleSizeChange"
                @current-change="handleCurrentChange"
                class="flex justify-center mt-4"
              />
            </div>
          </div>
        </div>

      </div>

      <!-- 详情模态框 -->
      <el-dialog
        v-model="showDetailModal"
        title="投诉详情"
        :width="isMobile ? '90%' : '600px'"
        :before-close="closeDetailModal"
        class="responsive-modal"
      >
        <div v-if="selectedComplaint" class="space-y-4">
          <div class="mb-4">
            <label class="font-semibold text-gray-700 block mb-2">投诉ID:</label>
            <span class="text-gray-600">{{ selectedComplaint.id }}</span>
          </div>

          <div class="mb-4">
            <label class="font-semibold text-gray-700 block mb-2">投诉人:</label>
            <div class="flex items-center">
              <div class="ml-3">
                <div class="font-medium">{{ selectedComplaint.unionid }}</div>
                <div class="text-sm text-gray-500">{{ selectedComplaint.mobile }}</div>
              </div>
            </div>
          </div>

          <div class="mb-4">
            <label class="font-semibold text-gray-700 block mb-2">活动名称:</label>
            <span class="text-gray-900">{{ selectedComplaint.activityName }}</span>
          </div>

          <div class="mb-4">
            <label class="font-semibold text-gray-700 block mb-2">投诉类型:</label>
            <el-tag :type="getTypeTagType(selectedComplaint.type)">
              {{ getTypeLabel(selectedComplaint.type) }}
            </el-tag>
          </div>

          <div class="mb-4">
            <label class="font-semibold text-gray-700 block mb-2">投诉内容:</label>
            <div class="bg-gray-50 p-3 rounded text-gray-900 leading-relaxed">
              {{ selectedComplaint.content }}
            </div>
          </div>

          <div class="mb-4">
            <label class="font-semibold text-gray-700 block mb-2">状态:</label>
            <el-tag :type="getStatusTagType(selectedComplaint.status)">
              {{ getStatusLabel(selectedComplaint.status) }}
            </el-tag>
          </div>

          <div class="mb-4">
            <label class="font-semibold text-gray-700 block mb-2">提交时间:</label>
            <span class="text-gray-600">{{ formatDate(selectedComplaint.createdAt * 1000) }}</span>
          </div>

          <div v-if="selectedComplaint.evidence" class="mb-4">
            <label class="font-semibold text-gray-700 block mb-2">证据材料:</label>
            <div class="bg-gray-50 p-3 rounded text-gray-900">
              {{ selectedComplaint.evidence }}
            </div>
          </div>

          <div v-if="selectedComplaint.images && selectedComplaint.images.length" class="mb-4">
            <label class="font-semibold text-gray-700 block mb-2">投诉图片:</label>
            <div class="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div
                v-for="(image, index) in selectedComplaint.images"
                :key="index"
                class="relative group cursor-pointer"
                @click="viewImage(image)"
              >
                <img
                  :src="image"
                  :alt="'投诉图片' + (index + 1)"
                  class="w-full h-32 object-cover rounded-lg border border-gray-200 hover:border-blue-300 transition-colors"
                />
                <div class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity rounded-lg flex items-center justify-center">
                  <i class="fas fa-search-plus text-white opacity-0 group-hover:opacity-100 transition-opacity"></i>
                </div>
              </div>
            </div>
          </div>

          <div v-if="selectedComplaint.adminNote" class="mb-4">
            <label class="font-semibold text-gray-700 block mb-2">处理备注:</label>
            <div class="bg-blue-50 p-3 rounded text-gray-900 border-l-4 border-blue-500">
              {{ selectedComplaint.adminNote }}
            </div>
          </div>
        </div>

        <template #footer>
          <span class="dialog-footer">
            <el-button @click="closeDetailModal">关闭</el-button>
          </span>
        </template>
      </el-dialog>

      <!-- 驳回模态框 -->
      <el-dialog
        v-model="showDismissModal"
        title="驳回投诉"
        :width="isMobile ? '90%' : '500px'"
        :before-close="closeDismissModal"
        class="responsive-modal"
      >
        <div class="mb-5">
          <label class="font-semibold text-gray-700 block mb-2">驳回理由:</label>
          <el-input
            v-model="dismissReason"
            type="textarea"
            :rows="4"
            placeholder="请输入驳回理由..."
            :maxlength="500"
            show-word-limit
          />
          <div v-if="dismissError" class="text-red-500 text-sm mt-2">
            {{ dismissError }}
          </div>
        </div>

        <template #footer>
          <span class="dialog-footer">
            <el-button @click="closeDismissModal">取消</el-button>
            <el-button type="danger" @click="confirmDismiss">确认驳回</el-button>
          </span>
        </template>
      </el-dialog>

      <!-- 图片查看模态框 -->
      <el-dialog
        v-model="showImageModal"
        :title="'查看投诉图片'"
        :width="isMobile ? '90%' : '700px'"
        :before-close="closeImageModal"
        class="responsive-modal"
      >
        <div class="flex justify-center">
          <img
            :src="currentImage"
            alt="投诉图片"
            class="max-w-full max-h-96 object-contain rounded-lg"
          />
        </div>

        <template #footer>
          <span class="dialog-footer">
            <el-button @click="closeImageModal">关闭</el-button>
          </span>
        </template>
      </el-dialog>
    </div>
  </div>
</template>

<style scoped>
@media (max-width: 768px) {
  .responsive-modal :deep(.el-dialog) {
    width: 90vw !important;
    max-width: 90vw !important;
    margin: 5vh auto !important;
    max-height: 90vh !important;
    overflow-y: auto !important;
  }

  .responsive-modal :deep(.el-dialog__body) {
    padding: 16px !important;
    max-height: calc(90vh - 120px) !important;
    overflow-y: auto !important;
  }

  .responsive-modal :deep(.el-dialog__header) {
    padding: 16px 16px 12px !important;
  }

  .responsive-modal :deep(.el-dialog__footer) {
    padding: 12px 16px 16px !important;
  }
}

@media (max-width: 480px) {
  .responsive-modal :deep(.el-dialog) {
    width: 95vw !important;
    max-width: 95vw !important;
    margin: 2vh auto !important;
  }

  .responsive-modal :deep(.el-dialog__body) {
    padding: 12px !important;
  }
}

/* Search dropdown styles */
.search-dropdown-container {
  position: relative;
}

.search-dropdown-button {
  min-width: 120px;
  justify-content: space-between;
}

/* Inline tags styling */
.inline-tags-container {
  min-height: 42px;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem 0.375rem;
  padding: 0.5rem 0.75rem;
  background: white;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  min-width: 256px;
}

.inline-tags-container:focus-within {
  outline: 2px solid #3b82f6;
  outline-offset: -1px;
}

.inline-tag {
  display: inline-flex;
  align-items: center;
  background-color: #dbeafe;
  color: #1e40af;
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  line-height: 1;
}

.inline-tag button {
  margin-left: 0.25rem;
  color: #3b82f6;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
}

.inline-tag button:hover {
  color: #1e40af;
  background-color: #bfdbfe;
}

.inline-tag-input {
  flex: 1;
  min-width: 120px;
  background: transparent;
  border: none;
  outline: none;
  font-size: 0.875rem;
  padding: 0.25rem 0;
}

.inline-edit-input {
  background: white;
  border: 1px solid #3b82f6;
  border-radius: 0.25rem;
  outline: none;
  font-size: 0.875rem;
  padding: 0.25rem 0.5rem;
  min-width: 100px;
}

.inline-tag.editing {
  background-color: #eff6ff;
  border-color: #3b82f6;
}
</style>

<script>
import { getAdminApiUrl } from "@/api/luckdraw.js";
import { getUserToken } from "@/utils/auth.js";
import SearchFilter from "@/components/Filters/SearchFilter.vue";

export default {
  name: 'Complaints',
  components: {
    SearchFilter
  },
  data() {
    return {
      complaints: [
        {
          id: 1,
          complainantName: '李四',
          complainantEmail: 'lisi@example.com',
          complainantAvatar: 'https://via.placeholder.com/40x40/10B981/FFFFFF?text=李',
          activityName: '周末户外徒步活动',
          type: 'fraud',
          content: '该活动组织者收取了报名费但未按承诺提供相应服务，存在欺诈行为。',
          status: 'pending',
          createdAt: '2024-01-15T10:30:00Z',
          evidence: '提供了转账记录和活动照片作为证据',
          images: [
            'https://via.placeholder.com/300x200/10B981/FFFFFF?text=转账记录',
            'https://via.placeholder.com/300x200/F59E0B/FFFFFF?text=活动照片1',
            'https://via.placeholder.com/300x200/EF4444/FFFFFF?text=活动照片2'
          ],
          adminNote: null
        },
        {
          id: 2,
          complainantName: '王五',
          complainantEmail: 'wangwu@example.com',
          complainantAvatar: 'https://via.placeholder.com/40x40/F59E0B/FFFFFF?text=王',
          activityName: '摄影分享会',
          type: 'inappropriate',
          content: '活动中有人发布了不当内容，影响活动氛围。',
          status: 'resolved',
          createdAt: '2024-01-14T15:20:00Z',
          evidence: '截图证据',
          images: [
            'https://via.placeholder.com/300x200/8B5CF6/FFFFFF?text=聊天截图1',
            'https://via.placeholder.com/300x200/06B6D4/FFFFFF?text=聊天截图2'
          ],
          adminNote: '已联系活动组织者处理，相关用户已被警告'
        },
        {
          id: 3,
          complainantName: '赵六',
          complainantEmail: 'zhaoliu@example.com',
          complainantAvatar: 'https://via.placeholder.com/40x40/EF4444/FFFFFF?text=赵',
          activityName: '读书分享会',
          type: 'spam',
          content: '有人在活动群中大量发送广告信息，影响正常交流。',
          status: 'dismissed',
          createdAt: '2024-01-13T09:15:00Z',
          evidence: '聊天记录截图',
          images: [
            'https://via.placeholder.com/300x200/84CC16/FFFFFF?text=广告截图'
          ],
          adminNote: '经核实，投诉内容不属实，已驳回'
        },
        {
          id: 4,
          complainantName: '钱七',
          complainantEmail: 'qianqi@example.com',
          complainantAvatar: 'https://via.placeholder.com/40x40/8B5CF6/FFFFFF?text=钱',
          activityName: '瑜伽体验课',
          type: 'other',
          content: '活动场地设施不完善，存在安全隐患。',
          status: 'pending',
          createdAt: '2024-01-12T14:45:00Z',
          evidence: '场地照片',
          images: [
            'https://via.placeholder.com/300x200/F97316/FFFFFF?text=场地照片1',
            'https://via.placeholder.com/300x200/A855F7/FFFFFF?text=场地照片2'
          ],
          adminNote: null
        },
        {
          id: 5,
          complainantName: '孙八',
          complainantEmail: 'sunba@example.com',
          complainantAvatar: 'https://via.placeholder.com/40x40/06B6D4/FFFFFF?text=孙',
          activityName: '美食制作工作坊',
          type: 'fraud',
          content: '活动组织者承诺提供食材但实际需要自备，存在虚假宣传。',
          status: 'resolved',
          createdAt: '2024-01-11T11:30:00Z',
          evidence: '活动宣传截图和实际现场照片',
          images: [
            'https://via.placeholder.com/300x200/EC4899/FFFFFF?text=宣传截图',
            'https://via.placeholder.com/300x200/14B8A6/FFFFFF?text=现场照片1',
            'https://via.placeholder.com/300x200/3B82F6/FFFFFF?text=现场照片2'
          ],
          adminNote: '已核实情况，已要求组织者退还部分费用并道歉'
        }
      ],
      searchQuery: '',
      statusFilter: '',
      typeFilter: '',
      currentPage: 1,
      itemsPerPage: 10,
      showDetailModal: false,
      showDismissModal: false,
      selectedComplaint: null,
      dismissReason: '',
      dismissError: '',
      dismissTargetComplaint: null,
      showImageModal: false,
      currentImage: '',
      loading: false,
      totalRecords: 0,

      // New search properties
      searchCriteria: [],
      searchKeys: [
        { value: 'unionid', label: '用户UnionID', type: 'text' },
        { value: 'mobile', label: '手机号', type: 'text' },
        { value: 'activityName', label: '活动名称', type: 'text' },
        { value: 'content', label: '投诉内容', type: 'text' },
        { value: 'tid', label: '活动ID', type: 'text' },
        { value: 'status', label: '投诉状态', type: 'multiselect' },
        { value: 'type', label: '投诉类型', type: 'select' },
        { value: 'createTime', label: '创建时间', type: 'date' },
        { value: 'updateTime', label: '更新时间', type: 'date' },
        { value: 'timeRange', label: '时间范围', type: 'daterange' }
      ],
      valueDropdownOptionsMap: {
        'status': [
          { value: 'pending', label: '待处理' },
          { value: 'resolved', label: '已处理' },
          { value: 'dismissed', label: '已驳回' }
        ],
        'type': [
          { value: '1', label: '包含大额奖项' },
          { value: '2', label: '虚假奖项' },
          { value: '3', label: '诱导分享' },
          { value: '4', label: '欺诈行为' },
          { value: '5', label: '发起人违规' },
          { value: '0', label: '其他原因' }
        ]
      },
      searchKeyLabelMap: {
        'unionid': '用户UnionID',
        'mobile': '手机号',
        'activityName': '活动名称',
        'content': '投诉内容',
        'tid': '活动ID',
        'status': '投诉状态',
        'type': '投诉类型'
      },
      showValueDropdown: false,
      valueDropdownOptions: [],
      selectedValue: '',
      editingIndex: -1 // Track which tag is being edited
    }
  },
  mounted() {
    this.loadComplaints();
  },
  computed: {
    filteredComplaints() {
      return this.complaints.filter(complaint => {
        const matchesSearch = complaint.content.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
                             complaint.activityName.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
                             complaint.complainantName.toLowerCase().includes(this.searchQuery.toLowerCase())
        const matchesStatus = !this.statusFilter || complaint.status === this.statusFilter
        const matchesType = !this.typeFilter || complaint.type === this.typeFilter

        return matchesSearch && matchesStatus && matchesType
      })
    },
    paginatedComplaints() {
      const start = (this.currentPage - 1) * this.itemsPerPage
      const end = start + this.itemsPerPage
      return this.filteredComplaints.slice(start, end)
    },
    totalPages() {
      return Math.ceil(this.filteredComplaints.length / this.itemsPerPage)
    },
    startIndex() {
      return (this.currentPage - 1) * this.itemsPerPage
    },
    endIndex() {
      return Math.min(this.startIndex + this.itemsPerPage, this.filteredComplaints.length)
    },
    visiblePages() {
      const pages = []
      const maxVisible = 5
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2))
      let end = Math.min(this.totalPages, start + maxVisible - 1)

      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1)
      }

      for (let i = start; i <= end; i++) {
        pages.push(i)
      }

      return pages
    },
    totalComplaints() {
      return this.complaints.length
    },
    pendingComplaints() {
      return this.complaints.filter(c => c.status === 'pending').length
    },
    resolvedComplaints() {
      return this.complaints.filter(c => c.status === 'resolved').length
    },
    dismissedComplaints() {
      return this.complaints.filter(c => c.status === 'dismissed').length
    },
    isMobile() {
      return window.innerWidth <= 768;
    }
  },


  methods: {
    async loadComplaints() {
      this.loading = true;

      try {
        const token = getUserToken();
        const url = new URL(getAdminApiUrl('/complaint/list'));
        url.searchParams.append('page', this.currentPage);
        url.searchParams.append('limit', this.itemsPerPage);

        console.log('Loading complaints with search criteria:', this.searchCriteria);

        // 添加搜索参数
        if (this.searchCriteria.length > 0) {
          // 处理多个搜索条件
          this.searchCriteria.forEach(criteria => {
            switch (criteria.key) {
              case 'unionid':
                url.searchParams.append('unionid', criteria.value);
                break;
              case 'mobile':
                url.searchParams.append('mobile', criteria.value);
                break;
              case 'activityName':
                url.searchParams.append('task_name', criteria.value);
                break;
              case 'content':
                url.searchParams.append('keyword', criteria.value);
                break;
              case 'tid':
                url.searchParams.append('tid', criteria.value);
                break;
            }
          });
        } else if (this.searchQuery) {
          // 向后兼容旧的搜索方式
          url.searchParams.append('keyword', this.searchQuery);
        }

        if (this.statusFilter) {
          url.searchParams.append('status', this.statusFilter);
        }

        console.log('API URL:', url.toString());

        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: {
            'Token': `${token}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error(`获取投诉列表失败: ${response.status}`);
        }

        const result = await response.json();

        if (result.sta === 0 && result.data.list) {
          this.totalRecords = result.data.total || result.data.count;
          this.complaints = result.data.list.map(complaint => ({
            id: complaint._id,
            content: complaint.desc,
            unionid: complaint.unionid,
            mobile: complaint.mobile,
            tid: complaint.tid,
            complainantName: complaint.complainant_name || complaint.username || '匿名用户',
            complainantEmail: complaint.complainant_email || complaint.email || '',
            activityName: complaint.task_name || '未知活动',
            type: complaint.reason_id,
            status: this.mapComplaintStatus(complaint.status),
            createdAt: complaint.t_add,
            evidence: complaint.evidence || '',
            images: complaint.pics || [],
            adminNote: complaint.admin_note || complaint.process_remark
          }));
        } else {
          throw new Error(result.msg || '获取投诉列表失败');
        }
      } catch (error) {
        console.error('加载投诉列表失败:', error);
        this.$message.error('加载投诉列表失败，使用演示数据');
        // 保持原有的演示数据
      } finally {
        this.loading = false;
      }
    },

    mapComplaintStatus(apiStatus) {
      const statusMap = {
        'pending': 'pending',
        'processing': 'pending',
        'resolved': 'resolved',
        'completed': 'resolved',
        'dismissed': 'dismissed',
        'rejected': 'dismissed'
      };
      return statusMap[apiStatus] || 'pending';
    },

    handleSearch() {
      this.currentPage = 1;
      this.loadComplaints();
    },

    handleFilterChange() {
      this.currentPage = 1;
      this.loadComplaints();
    },


    executeSearch(searchCriteria) {
      this.searchCriteria = searchCriteria;
      console.log('Executing search with criteria:', this.searchCriteria);
      this.currentPage = 1;
      this.loadComplaints();
    },

    clearSearch() {
      this.searchCriteria = [];
      this.currentPage = 1;
      this.loadComplaints();
    },




    viewImage(image) {
      this.currentImage = image;
      this.showImageModal = true;
    },
    closeImageModal() {
      this.showImageModal = false;
      this.currentImage = '';
    },
    getTypeLabel(type) {
      const labels = {
        1: '包含大额奖项（总额超过5万元）',
        2: '虚假奖项',
        3: '诱导分享、裂变',
        4: '欺诈行为（要求支付钱款等）',
        5: '发起人有前序违规行为',
        0: '其他原因'
      }
      return labels[type] || type
    },
    getStatusLabel(status) {
      const labels = {
        pending: '待处理',
        resolved: '已处理',
        dismissed: '已驳回'
      }
      return labels[status] || status
    },
    getTypeColor(type) {
      const colors = {
        fraud: { bg: '#fef2f2', text: '#dc2626' },
        inappropriate: { bg: '#fffbeb', text: '#d97706' },
        spam: { bg: '#fff7ed', text: '#ea580c' },
        other: { bg: '#f3f4f6', text: '#374151' }
      }
      return colors[type] || { bg: '#f3f4f6', text: '#374151' }
    },
    getStatusColor(status) {
      const colors = {
        pending: { bg: '#fffbeb', text: '#d97706' },
        resolved: { bg: '#f0fdf4', text: '#16a34a' },
        dismissed: { bg: '#f3f4f6', text: '#374151' }
      }
      return colors[status] || { bg: '#f3f4f6', text: '#374151' }
    },
    getTypeTagType(type) {
      const tagTypes = {
        fraud: 'danger',
        inappropriate: 'warning',
        spam: 'warning',
        other: 'info'
      }
      return tagTypes[type] || 'info'
    },
    getStatusTagType(status) {
      const tagTypes = {
        pending: 'warning',
        resolved: 'success',
        dismissed: 'info'
      }
      return tagTypes[status] || 'info'
    },
    formatDate(dateString) {
      const date = new Date(dateString)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    viewComplaint(complaint) {
      console.log('查看投诉:', complaint)
      this.selectedComplaint = complaint
      this.showDetailModal = true
      console.log('模态框状态:', this.showDetailModal)
    },
    closeDetailModal() {
      this.showDetailModal = false
      this.selectedComplaint = null
    },
    async resolveComplaint(complaint) {
      if (confirm('确认处理此投诉？')) {
        this.loading = true;

        try {
          const token = getUserToken();
          const response = await fetch(getAdminApiUrl(`/complaint/${complaint.id}/resolve`), {
            method: 'POST',
            headers: {
              'Token': `${token}`,
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            },
            credentials: 'include'
          });

          if (!response.ok) {
            throw new Error(`处理投诉失败: ${response.status}`);
          }

          const result = await response.json();

          if (result.sta === 0) {
            complaint.status = 'resolved';
            complaint.adminNote = result.data?.admin_note || '已处理完成';
            this.$message.success('投诉已处理完成');
            this.loadComplaints(); // 重新加载数据
          } else {
            throw new Error(result.msg || '处理投诉失败');
          }
        } catch (error) {
          console.error('处理投诉失败:', error);
          this.$message.error('处理投诉失败，请重试');
        } finally {
          this.loading = false;
        }
      }
    },
    dismissComplaint(complaint) {
      this.dismissTargetComplaint = complaint
      this.dismissReason = ''
      this.dismissError = ''
      this.showDismissModal = true
    },
    closeDismissModal() {
      this.showDismissModal = false
      this.dismissTargetComplaint = null
      this.dismissReason = ''
      this.dismissError = ''
    },
    async confirmDismiss() {
      if (!this.dismissReason.trim()) {
        this.dismissError = '请输入驳回理由'
        return
      }

      this.loading = true;

      try {
        const token = getUserToken();
        const response = await fetch(getAdminApiUrl(`/complaint/${this.dismissTargetComplaint.id}/dismiss`), {
          method: 'POST',
          headers: {
            'Token': `${token}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            reason: this.dismissReason
          }),
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error(`驳回投诉失败: ${response.status}`);
        }

        const result = await response.json();

        if (result.sta === 0) {
          this.dismissTargetComplaint.status = 'dismissed';
          this.dismissTargetComplaint.adminNote = `驳回理由: ${this.dismissReason}`;
          this.closeDismissModal();
          this.$message.success('投诉已驳回');
          this.loadComplaints(); // 重新加载数据
        } else {
          throw new Error(result.msg || '驳回投诉失败');
        }
      } catch (error) {
        console.error('驳回投诉失败:', error);
        this.$message.error('驳回投诉失败，请重试');
      } finally {
        this.loading = false;
      }
    },
    refreshData() {
      this.currentPage = 1;
      this.loadComplaints();
    },
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
      }
    },
    goToPage(page) {
      this.currentPage = page
    },
    onPageChange(page) {
      this.currentPage = page
    },
    handleSizeChange(size) {
      this.itemsPerPage = size
      this.currentPage = 1
      this.loadComplaints()
    },
    handleCurrentChange(page) {
      this.currentPage = page
      this.loadComplaints()
    }
  }
}
</script>
